import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {AnalyticsModalType} from 'analytics/AnalyticsTypes';
import {ModalMetadataEntryType} from 'analytics/AnalyticsTypes';
import {SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  canCurrentUserUnrestrictSample,
  getEditSampleRestrictionStatusDisabledMessage,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import notifications from 'notifications';
import {ContainerPropertiesUpdate} from 'samples/ContainerDAO';
import {bulkUpdateContainerPropertiesWithDataMods} from 'samples/StorableActions';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {InputChangeEvent} from 'util/Types';

import BulkUpdateSampleRolesModal, {
  BaseBulkUpdateSampleRolesModalProps,
  BulkUpdateSampleRolesAction,
  computeNewPartyIds,
  ItemWithSampleRolesAndPermissions,
} from './BulkUpdateSampleRolesModal';

export interface BulkUpdateRestrictedSampleUsersModalProps extends BaseBulkUpdateSampleRolesModalProps {
  items: Array<ItemWithSampleRolesAndPermissions>;

  /** See comment on  this prop in BulkUpdateSampleRoleModal.tsx */
  alternateParties?: {
    label: string;
    partyIdsByItemId: {[itemId: string]: Set<string>};
    disabled?: boolean;
    analyticsMetadataEntryType: ModalMetadataEntryType;
  };
}

export default function BulkUpdateRestrictedSampleUsersModal(
  props: BulkUpdateRestrictedSampleUsersModalProps
): JSX.Element {
  const [action, setAction] = useState<BulkUpdateSampleRolesAction>(BulkUpdateSampleRolesAction.ADD);
  const [isClearRestrictionStatusSelected, setIsClearRestrictionStatusSelected] = useState<boolean>(false);

  const shouldShowClearRestrictionCheckbox =
    action === BulkUpdateSampleRolesAction.CLEAR && hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2);
  const isUnrestrictingSamplesOnSubmit =
    shouldShowClearRestrictionCheckbox && isClearRestrictionStatusSelected;

  const analyticsMetadata = {
    ...props.analyticsMetadata,
    modalType: AnalyticsModalType.BULK_UPDATE_RESTRICTED_SAMPLE_USERS,
  };

  const getNoticeFn = (selectedSampleIds: Array<string>): {header: string; content?: string} | undefined => {
    const selectedItems = props.items.filter(({id}) => selectedSampleIds.includes(id));
    const shouldShowNoticeAboutRestrictingSamples =
      [BulkUpdateSampleRolesAction.ADD, BulkUpdateSampleRolesAction.REPLACE].includes(action) &&
      hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) &&
      selectedItems.some((item) => item.restrictionStatus === SampleRestrictionStatus.UNRESTRICTED);
    return shouldShowNoticeAboutRestrictingSamples
      ? {
          header: 'Sample restriction will be enabled for some items.',
          content:
            "This action will enable sample restriction on some items that currently have it disabled. If you don't want to enable sample restriction, de-select these items before continuing.",
        }
      : isUnrestrictingSamplesOnSubmit
        ? {
            header: 'Disabling sample restriction will make these items available for everyone.',
          }
        : undefined;
  };

  const handleRequestSubmit = (
    selectedItems: Array<ItemWithSampleRolesAndPermissions>,
    action: BulkUpdateSampleRolesAction,
    partyIdsByItemId: {[itemId: string]: Set<string>}
  ): void => {
    if (isUnrestrictingSamplesOnSubmit) {
      for (const item of selectedItems) {
        if (!canCurrentUserUnrestrictSample(item)) {
          notifications.error(
            getEditSampleRestrictionStatusDisabledMessage(SampleRestrictionStatus.RESTRICTED)
          );
          props.onRequestClose();
          return;
        }
      }
    }

    const containerUpdates: Array<ContainerPropertiesUpdate> = selectedItems.map((item) => ({
      id: item.id,
      sampleRoles: {
        restrictedSampleUserIds: computeNewPartyIds(
          action,
          item.sampleRoles.restrictedSampleUsers.map(({id}) => id),
          partyIdsByItemId[item.id]
        ),
      },
      ...(isUnrestrictingSamplesOnSubmit
        ? {
            restrictionStatus: SampleRestrictionStatus.UNRESTRICTED,
          }
        : {}),
    }));
    bulkUpdateContainerPropertiesWithDataMods(containerUpdates, analyticsMetadata);
    props.onRequestClose();
  };

  return (
    <BulkUpdateSampleRolesModal
      {...props}
      label={getRestrictedSampleUsersDisplayText()}
      onChangeAction={(action) => {
        setAction(action);
      }}
      onRequestSubmit={handleRequestSubmit}
      getItemValueFn={(item) => item.sampleRoles.restrictedSampleUsers}
      onRequestClose={props.onRequestClose}
      getNoticeFn={getNoticeFn}
      analyticsMetadata={analyticsMetadata}
      allowBenchlingApps={true}
    >
      {shouldShowClearRestrictionCheckbox && (
        <CheckboxInput
          className={css(styles.paddingTop15px)}
          id="bulk-edit-rsus-disable-sample-restriction-checkbox"
          checked={isClearRestrictionStatusSelected}
          label="Disable sample restriction"
          onChange={(e: InputChangeEvent) => {
            setIsClearRestrictionStatusSelected(e.target.checked);
          }}
        />
      )}
    </BulkUpdateSampleRolesModal>
  );
}

const styles = StyleSheet.create({
  paddingTop15px: {
    paddingTop: '15px',
  },
});
